// Upload + playback component for Cloudflare Stream videos. VideoViewer is the
// read-only counterpart — keep skeleton appearance and playback behaviour in
// sync between the two when making changes here.
import './VideoUpload.css';
import '../PhotoUpload/PhotoUpload.css';
import 'plyr/dist/plyr.css';

import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

// @ts-ignore - Plyr default export not recognized by types
import Plyr from 'plyr';

import { Firestore } from 'firebase/firestore';

import { useVideoUploads } from '../../../contexts/VideoUploadContext';
import {
  deleteStreamVideo,
  getStreamPlaybackToken,
  getStreamVideoStatus,
} from '../../../services/streamService';
import { VideoScoreValue } from '../../../types/athlete';

import CameraRecorder from '../CameraRecorder/CameraRecorder';
import SpinnerIcon from '../SpinnerIcon';
import { getVideoQuality } from '../../../utils/videoQualitySettings';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import IconButton from '../IconButton/IconButton';
import TrashIcon from '../TrashIcon';
import VideoUploadButton from '../VideoUploadButton/VideoUploadButton';

const POLL_INTERVAL_MS = 3000;

export interface VideoUploadProps {
  tryoutId: string;
  coachUid: string;
  videoUid?: string;
  disabled?: boolean;
  label?: string;
  compactEmpty?: boolean;
  statusText?: string;
  /**
   * Required for navigation-resilient uploads. The provider writes the
   * Firestore field `scores.<fieldId>.video` directly when the upload finishes,
   * so the upload survives unmounts of this component.
   */
  db: Firestore;
  athleteId: string;
  athleteName: string;
  fieldId: string;
  onVideoChange: (value: VideoScoreValue | null) => void;
  onUploadingChange?: (uploading: boolean) => void;
}

/**
 * Phase machine:
 *   idle       — no video, show upload button
 *   preview    — recording captured, show fullscreen preview with Retake/Use buttons
 *   uploading  — file being sent to Cloudflare; local-blob video shown expanded
 *   checking   — videoUid received, asking backend if it's ready
 *   processing — confirmed not ready, poll until ready
 *   ready      — playback available (local blob and/or signed iframe url)
 *   removing   — optimistic delete, show empty state until parent clears videoUid
 *
 * Inline player layout:
 *   - Local blob (this session): always rendered expanded (40% row width).
 *   - Iframe-only (page reload, mounted with videoUid): starts as a small
 *     150×100 thumbnail (Cloudflare's player loads the first frame as a
 *     poster). One click expands it inline; it stays expanded for the rest
 *     of the page load.
 */
type Phase = 'idle' | 'preview' | 'uploading' | 'checking' | 'processing' | 'ready' | 'removing';

const VideoUpload: React.FC<VideoUploadProps> = ({
  tryoutId,
  coachUid,
  videoUid,
  disabled = false,
  label = 'Athlete video',
  compactEmpty = false,
  statusText,
  db,
  athleteId,
  athleteName,
  fieldId,
  onVideoChange,
  onUploadingChange,
}) => {
  const { startUpload: startManagedUpload, getEntry } = useVideoUploads();
  const uploadKey = `${tryoutId}:${athleteId}:${fieldId}`;
  const uploadEntry = getEntry(uploadKey);
  const initialPhase: Phase = videoUid
    ? 'checking'
    : uploadEntry?.status === 'uploading'
      ? 'uploading'
      : 'idle';
  const [phase, setPhase] = useState<Phase>(initialPhase);
  const [localUrl, setLocalUrl] = useState<string | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const uploadProgress = uploadEntry?.progress ?? 0;
  const [cameraOpen, setCameraOpen] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [iframeExpanded, setIframeExpanded] = useState(false);
  const [currentBlob, setCurrentBlob] = useState<{ blob: Blob; mimeType: string } | null>(null);
  const [aspect, setAspect] = useState<{ width: number; height: number } | null>(null);

  const phaseRef = useRef<Phase>(videoUid ? 'checking' : 'idle');
  const localUrlRef = useRef<string | null>(null);
  const prevVideoUidRef = useRef<string | undefined>(videoUid);
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const plyrRef = useRef<any>(null);

  const transitionTo = (next: Phase): void => {
    phaseRef.current = next;
    setPhase(next);
  };

  const commitLocalUrl = (url: string | null): void => {
    if (localUrlRef.current && localUrlRef.current !== url) {
      URL.revokeObjectURL(localUrlRef.current);
    }
    localUrlRef.current = url;
    setLocalUrl(url);
  };

  // Plyr on the recording-preview video when the capture-preview modal opens.
  useEffect(() => {
    if (phase !== 'preview' || !previewVideoRef.current) return;

    if (plyrRef.current) plyrRef.current.destroy();

    plyrRef.current = new Plyr(previewVideoRef.current, {
      controls: ['play', 'play-large', 'progress', 'current-time', 'duration', 'settings', 'mute'],
      autoplay: false,
      autopause: false,
      clickToPlay: true,
      muted: true,
      speed: { selected: 1, options: [0.5, 1, 1.5, 2] },
      settings: ['speed', 'loop'],
      fullscreen: { enabled: false, iosNative: false },
      keyboard: { focused: false, global: false },
      loop: { active: true },
      hideControls: false,
    });

    return () => {
      if (plyrRef.current) {
        plyrRef.current.destroy();
        plyrRef.current = null;
      }
    };
  }, [phase]);

  useEffect(() => () => {
    if (localUrlRef.current) URL.revokeObjectURL(localUrlRef.current);
  }, []);

  useEffect(() => {
    const prev = prevVideoUidRef.current;
    prevVideoUidRef.current = videoUid;

    if (videoUid === prev) return;

    if (!videoUid) {
      if (phaseRef.current !== 'uploading') {
        commitLocalUrl(null);
        setIframeUrl(null);
        setAspect(null);
        setIframeExpanded(false);
        transitionTo('idle');
      }
    } else {
      setIframeUrl(null);
      setIframeExpanded(false);
      transitionTo('checking');
    }
  }, [videoUid]); // eslint-disable-line react-hooks/exhaustive-deps

  // One-shot status check when entering 'checking' phase.
  useEffect(() => {
    if (phase !== 'checking' || !videoUid) return;

    let cancelled = false;

    const check = async (): Promise<void> => {
      try {
        const status = await getStreamVideoStatus(videoUid);
        if (cancelled) return;

        if (status.width && status.height) {
          setAspect({ width: status.width, height: status.height });
        }

        if (status.readyToStream) {
          if (localUrlRef.current) {
            transitionTo('ready');
          } else {
            const { iframeUrl: url } = await getStreamPlaybackToken(videoUid);
            if (cancelled) return;
            setIframeUrl(url);
            transitionTo('ready');
          }
        } else {
          transitionTo('processing');
        }
      } catch {
        if (!cancelled) transitionTo('processing');
      }
    };

    void check();
    return () => { cancelled = true; };
  }, [phase, videoUid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll while in 'processing' phase.
  useEffect(() => {
    if (phase !== 'processing' || !videoUid) return;

    let cancelled = false;

    const poll = async (): Promise<void> => {
      try {
        const status = await getStreamVideoStatus(videoUid);
        if (cancelled || !status.readyToStream) return;

        if (status.width && status.height) {
          setAspect({ width: status.width, height: status.height });
        }

        if (localUrlRef.current) {
          transitionTo('ready');
        } else {
          const { iframeUrl: url } = await getStreamPlaybackToken(videoUid);
          if (cancelled) return;
          setIframeUrl(url);
          transitionTo('ready');
        }
      } catch {
        // Ignore transient errors; next tick will retry
      }
    };

    const id = setInterval(() => { void poll(); }, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [phase, videoUid]); // eslint-disable-line react-hooks/exhaustive-deps

  // React to managed-upload completion/error. The provider writes Firestore
  // directly on success, so the new videoUid flows back through props and the
  // existing videoUid effect handles the transition to 'checking'. We only
  // need to surface errors and to mirror the 'uploading' status if we
  // remounted mid-upload.
  useEffect(() => {
    if (!uploadEntry) return;
    if (uploadEntry.status === 'uploading' && phaseRef.current !== 'uploading') {
      transitionTo('uploading');
      onUploadingChange?.(true);
    }
    if (uploadEntry.status === 'done') {
      onUploadingChange?.(false);
    }
    if (uploadEntry.status === 'error') {
      commitLocalUrl(null);
      transitionTo('idle');
      setError(uploadEntry.errorMessage ?? 'Upload failed. Please try again.');
      onUploadingChange?.(false);
    }
  }, [uploadEntry?.status, uploadEntry?.errorMessage]); // eslint-disable-line react-hooks/exhaustive-deps


  const handleCapture = (blob: Blob, mimeType: string): void => {
    commitLocalUrl(URL.createObjectURL(blob));
    setCameraOpen(false);
    setCurrentBlob({ blob, mimeType });
    transitionTo('preview');
  };

  const handleRetake = (): void => {
    setCurrentBlob(null);
    commitLocalUrl(null);
    setCameraOpen(true);
    transitionTo('idle');
  };

  const handleUseVideo = (): void => {
    if (!currentBlob) return;
    const { blob, mimeType } = currentBlob;
    setCurrentBlob(null);
    transitionTo('uploading');
    setError(null);
    onUploadingChange?.(true);
    startManagedUpload({
      key: uploadKey,
      blob,
      mimeType,
      coachUid,
      athleteName,
      firestore: { db, tryoutId, athleteId, fieldId },
    });
  };

  const handleRemoveConfirmed = (): void => {
    setRemoveConfirmOpen(false);
    const uidToDelete = videoUid;
    commitLocalUrl(null);
    setIframeUrl(null);
    setAspect(null);
    setIframeExpanded(false);
    transitionTo('removing');
    onVideoChange(null);
    if (uidToDelete) {
      void deleteStreamVideo(uidToDelete).catch(() => { /* best-effort */ });
    }
  };

  // Fall back to intrinsic dimensions from the local <video> if the backend
  // hasn't returned dimensions yet.
  const handleLocalVideoMetadata = (e: React.SyntheticEvent<HTMLVideoElement>): void => {
    const v = e.currentTarget;
    if (v.videoWidth && v.videoHeight && !aspect) {
      setAspect({ width: v.videoWidth, height: v.videoHeight });
    }
  };

  const hasVideo = phase !== 'idle' && phase !== 'removing' && phase !== 'preview';
  const busy = phase === 'uploading' || phase === 'removing';

  // Iframe-only path starts collapsed and expands on first click. The local
  // blob renders inline at its own fixed width (`.video-upload-player--local`)
  // and does not flip the shell into the expanded layout on its own.
  const showIframeAsThumbnail = !localUrl && Boolean(iframeUrl) && !iframeExpanded;
  const playerExpanded = Boolean(iframeUrl) && iframeExpanded;

  const aspectStyle: React.CSSProperties = aspect
    ? { aspectRatio: `${aspect.width} / ${aspect.height}` }
    : { aspectRatio: '9 / 16' };

  const rootClass = [
    'photo-upload',
    compactEmpty && !hasVideo ? 'photo-upload--compact-empty' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={rootClass}>
      {label ? <div className="photo-upload-label">{label}</div> : null}

      {!hasVideo ? (
        <div className="photo-upload-container">
          <VideoUploadButton
            className="photo-upload-button"
            onClick={() => setCameraOpen(true)}
            disabled={disabled || busy}
            ariaLabel={label}
          />
        </div>
      ) : (
        <div
          className={`video-upload-shell${playerExpanded ? ' video-upload-shell--expanded' : ''}`}
        >
          <div className="video-upload-player-col">
            {localUrl ? (
              <div className="video-upload-player video-upload-player--local" style={aspectStyle}>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                  className="video-upload-player-media"
                  src={localUrl}
                  controls
                  playsInline
                  muted={true}
                  autoPlay={false}
                  disablePictureInPicture={false}
                  preload="metadata"
                  onLoadedMetadata={handleLocalVideoMetadata}
                />
              </div>
            ) : iframeUrl ? (
              showIframeAsThumbnail ? (
                <button
                  type="button"
                  className="video-upload-thumb-button"
                  onClick={() => setIframeExpanded(true)}
                  aria-label={`Expand ${label}`}
                >
                  <span className="video-upload-thumb-frame" style={aspectStyle}>
                    <iframe
                      className="video-upload-thumb-iframe"
                      src={`${iframeUrl}?muted=true&preload=metadata&controls=false`}
                      allow="accelerometer; gyroscope; encrypted-media;"
                      title={label}
                      tabIndex={-1}
                    />
                    <span className="video-upload-thumb-play-overlay" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="28" height="28">
                        <path d="M8 5v14l11-7z" fill="currentColor" />
                      </svg>
                    </span>
                  </span>
                </button>
              ) : (
                // Expanded iframe: wrapper takes the video's true aspect ratio,
                // and the iframe inside is sized so its inner video region fills
                // the wrapper exactly. Cloudflare's 16:9 letterbox bars overflow
                // past the wrapper edges and get clipped by overflow:hidden.
                <div
                  className="video-upload-player video-upload-player--expanded"
                  style={aspectStyle}
                >
                  <iframe
                    className="video-upload-player-media video-upload-player-media--iframe"
                    src={`${iframeUrl}?autoplay=true&muted=true&preload=metadata`}
                    allow="accelerometer; gyroscope; encrypted-media;"
                    allowFullScreen
                    title={label}
                  />
                </div>
              )
            ) : (
              // checking/processing without a local copy or iframe yet
              <div className="video-upload-player video-upload-player--skeleton" style={aspectStyle}>
                <SpinnerIcon className="video-upload-spinner video-upload-spinner--spin" />
              </div>
            )}
          </div>

          <div className="photo-upload-side-actions video-upload-side-actions">
            {phase === 'uploading' ? (
              <svg className="video-upload-spinner video-upload-spinner--uploading" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" strokeOpacity="0.2" />
                <circle
                  cx="12" cy="12" r="10" fill="none"
                  stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray="62.83"
                  strokeDashoffset={62.83 * (1 - uploadProgress / 100)}
                  transform="rotate(-90 12 12)"
                  style={{ transition: 'stroke-dashoffset 0.3s ease' }}
                />
              </svg>
            ) : (
              <IconButton
                className="photo-upload-remove"
                aria-label="Remove video"
                title="Remove video"
                onClick={() => setRemoveConfirmOpen(true)}
                disabled={disabled || busy}
              >
                <TrashIcon />
              </IconButton>
            )}
            {statusText ? (
              <span className="photo-upload-side-status">{statusText}</span>
            ) : null}
          </div>
        </div>
      )}

      {error ? (
        <div className="video-upload-error" role="alert">{error}</div>
      ) : null}

      {phase === 'preview' && localUrl ? createPortal(
        <div className="video-upload-preview-modal">
          <div className="video-upload-preview-container">
            <div className="video-upload-preview-video-wrapper">
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video
                ref={previewVideoRef}
                className="video-upload-preview-video"
                src={localUrl}
                playsInline
                autoPlay
                disablePictureInPicture
                disableRemotePlayback
                controlsList="nodownload nofullscreen noremoteplayback"
                {...{ 'x-webkit-airplay': 'deny' }}
              />
            </div>
            <div className="video-upload-preview-bar">
              <div
                className="video-upload-preview-action"
                onClick={handleRetake}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleRetake(); }}
              >
                Retake
              </div>
              <div
                className="video-upload-preview-action video-upload-preview-action--primary"
                onClick={handleUseVideo}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleUseVideo(); }}
              >
                Use Video
              </div>
            </div>
          </div>
        </div>,
        document.body,
      ) : null}

      <ConfirmDialog
        open={removeConfirmOpen}
        title="Remove video?"
        message="Are you sure you want to remove this video?"
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={handleRemoveConfirmed}
        onCancel={() => setRemoveConfirmOpen(false)}
      />

      {cameraOpen ? (
        <CameraRecorder
          quality={getVideoQuality()}
          onCapture={handleCapture}
          onClose={() => setCameraOpen(false)}
        />
      ) : null}
    </div>
  );
};

export default VideoUpload;
